import React, { memo, ReactNode, useContext, useMemo, VFC } from 'react'
import styled from 'styled-components'

import { ProductContext } from '@Components/Product/Context'
import { RelatedProductsList } from '@Components/Product/RelatedProducts/List'
import { Asset, Maybe, Product, useProductsFromSameCategoryQuery, useRecommendedForYouQuery } from '@Generated/graphql'
import { getLinkedProductIDs } from '@Globals/CMS/ProductHelpers'
import { up } from '@Globals/Style/breakpoints'
import { useTranslation } from '@Globals/Translations'

const RelatedWrapper = styled.section(({ theme }) => `
  max-width: ${theme.cmsModule.width.wide};
  width: 100%;

  & + & {
    ${up('mobileStyleThreshold')} {
      margin-top: 40px;
    }
  }
`)

const RelatedHeader = styled.h3(({ theme }) => `
  padding: 0 15px;
  margin-bottom: 3px;
  text-transform: uppercase;
  font: inherit;
  font-size: .875rem;
  color: ${theme.colors.greyDark};

  ${up('mobileStyleThreshold')} {
    margin-bottom: 10px;
    text-align: center;
    font-weight: bold;
    font-size: 1.25rem;
  }
`)

// These come from the CMS. It's outta my hands, man.
export type RelatedProductsListType = 'same-category' | 'recommended-for-you'

type Props = {
  id?: string
  type: RelatedProductsListType
}

export type RelatedProduct = Pick<Product, 'id' | 'name' | 'type' | 'url' | 'brand' | 'masterVariant'> & {
  moneyShotAsset?: Maybe<Pick<Asset, 'url'>>
  primaryAsset?: Maybe<Pick<Asset, 'url'>>
}

export const RelatedProducts: VFC<Props> = memo(({ id, type }) => {
  const parentProduct = useContext(ProductContext)
  const { id: productID = '' } = parentProduct || {}
  const { t } = useTranslation('products')
  const linkedProductsOnParent = useMemo(() => getLinkedProductIDs(parentProduct), [parentProduct])

  const { data: recommendedProducts } = useRecommendedForYouQuery({
    variables: { productID, excludeIDs: linkedProductsOnParent },
    skip: type !== 'recommended-for-you',
  })
  const { data: productsFromSameCategory } = useProductsFromSameCategoryQuery({
    variables: { productID, excludeIDs: linkedProductsOnParent },
    skip: type !== 'same-category',
  })

  if (!recommendedProducts && !productsFromSameCategory) {
    return null
  }

  let content: ReactNode

  if (recommendedProducts && (recommendedProducts.product?.recommendedForYou?.products || []).length) {
    content = (
      <RelatedProductsList products={recommendedProducts.product?.recommendedForYou?.products || []} />
    )
  }

  if (productsFromSameCategory && (productsFromSameCategory.product?.sameCategory?.products || []).length) {
    content = (
      <RelatedProductsList products={productsFromSameCategory.product?.sameCategory?.products || []} />
    )
  }

  // Abort now if we haven't really got any content to show.
  if (!content) {
    return null
  }

  return (
    <RelatedWrapper id={id}>
      <RelatedHeader>
        {t(type === 'recommended-for-you' ? 'RECOMMENDED_FOR_YOU' : 'PP_PRODUCTS_FROM_SAME_CAT_TITLE')}
      </RelatedHeader>
      {content}
    </RelatedWrapper>
  )
})
